class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    // Check if this is a sorting event
    const isSortingEvent = event.target && (
      event.target.matches('.facet-filters__sort') ||
      event.target.closest('.facet-filters__sort') ||
      event.target.closest('#FacetSortForm') ||
      event.target.closest('#FacetSortDrawerForm') ||
      event.target.name === 'sort_by'
    );

    // For sorting, use AJAX (fast)
    if (isSortingEvent) {
      FacetFiltersForm.renderPage(searchParams, event);
    } else {
      // For filters, reload the page (ensures everything syncs properly)
      const url = new URL(window.location);
      url.search = searchParams;
      window.location.href = url.toString();
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    if (event.srcElement && event.srcElement.className == 'mobile-facets__checkbox') {
      const form = event.target.closest('form');
      if (form) {
        const searchParams = this.createSearchParams(form);
        this.onSubmitForm(searchParams, event);
      }
    } else {
      const forms = [];
      const targetForm = event.target ? event.target.closest('form') : null;
      const isMobile = targetForm && targetForm.id === 'FacetFiltersFormMobile';

      // Collect parameters from all relevant forms
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          // Desktop: collect from both filter and sort forms
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const formParams = this.createSearchParams(form);
            if (formParams) {
              forms.push(formParams);
            }
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          // Mobile: collect from mobile form
          const formParams = this.createSearchParams(form);
          if (formParams) {
            forms.push(formParams);
          }
        }
      });

      // Merge all parameters and remove duplicates
      const allParams = new URLSearchParams();

      // First, preserve current URL parameters (especially sort_by)
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, key) => {
        allParams.set(key, value);
      });

      // Check if this is a sorting event
      const isSortingEvent = event.target && (
        event.target.matches('.facet-filters__sort') ||
        event.target.closest('.facet-filters__sort') ||
        event.target.closest('#FacetSortForm') ||
        event.target.closest('#FacetSortDrawerForm') ||
        event.target.name === 'sort_by'
      );

      // Then add/update with form parameters
      forms.forEach(paramString => {
        const params = new URLSearchParams(paramString);
        params.forEach((value, key) => {
          if (key === 'sort_by') {
            // Only update sort_by if this is explicitly a sorting event
            if (isSortingEvent) {
              allParams.set(key, value);
            }
            // Otherwise, ignore sort_by from forms and keep the current URL value
          } else if (key.startsWith('filter.') || key.startsWith('option') || key === 'q') {
            // For filters, replace existing values
            allParams.delete(key);
            allParams.append(key, value);
          } else {
            // For other parameters, append
            allParams.append(key, value);
          }
        });
      });

      this.onSubmitForm(allParams.toString(), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);

// Clean Dropdown Facet Handler
class FacetDropdownHandler {
  constructor() {
    this.initializeDropdowns();
  }

  initializeDropdowns() {
    const dropdownSelects = document.querySelectorAll('.facets__clean-select');
    dropdownSelects.forEach(select => {
      select.addEventListener('change', this.handleDropdownChange.bind(this));
    });
  }

  handleDropdownChange(event) {
    const select = event.target;
    const selectedValue = select.value;
    const paramName = select.dataset.paramName;
    
    // Get current URL parameters
    const url = new URL(window.location);
    const params = new URLSearchParams(url.search);
    
    // Clear existing parameters for this filter
    params.delete(paramName);
    
    // Add new selected value if not empty
    if (selectedValue && selectedValue !== '') {
      params.append(paramName, selectedValue);
    }
    
    // Update URL and reload results
    const newSearchParams = params.toString();
    FacetFiltersForm.renderPage(newSearchParams, event);
  }

  static updateDropdowns(html) {
    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(html, 'text/html');
    
    // Find all clean dropdown selects in both current page and fetched content
    const currentDropdowns = document.querySelectorAll('.facets__clean-select');
    const newDropdowns = parsedHTML.querySelectorAll('.facets__clean-select');
    
    // Update each dropdown with new options and selections
    currentDropdowns.forEach(currentSelect => {
      const matchingNewSelect = Array.from(newDropdowns).find(
        newSelect => newSelect.id === currentSelect.id
      );
      
      if (matchingNewSelect) {
        // Preserve the current selected value
        const currentValue = currentSelect.value;
        
        // Replace the options
        currentSelect.innerHTML = matchingNewSelect.innerHTML;
        
        // Restore selection if still available
        if (currentValue) {
          const optionExists = Array.from(currentSelect.options).some(opt => opt.value === currentValue);
          if (optionExists) {
            currentSelect.value = currentValue;
          }
        }
      }
    });
  }
}

// Initialize dropdown handler when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new FacetDropdownHandler();
  // Also initialize mobile dropdowns
  initializeMobileDropdowns();
});

// Mobile Radio Button Handler
function initializeMobileDropdowns() {
  const mobileRadioInputs = document.querySelectorAll('.mobile-facets__radio-input');
  mobileRadioInputs.forEach(radio => {
    radio.addEventListener('change', handleMobileRadioChange);
  });
  
  // Initialize price sliders
  initializePriceSliders();
}

// Price Slider Handler
function initializePriceSliders() {
  const minSlider = document.getElementById('price-min-slider');
  const maxSlider = document.getElementById('price-max-slider');
  const minValue = document.getElementById('price-min-value');
  const maxValue = document.getElementById('price-max-value');
  const sliderRange = document.querySelector('.price-slider-range');

  if (minSlider && maxSlider && minValue && maxValue && sliderRange) {
    const currency = minSlider.dataset.currency || '$';
    const maxPrice = parseInt(minSlider.max);

    function updateSliderValues() {
      const minVal = parseInt(minSlider.value);
      const maxVal = parseInt(maxSlider.value);

      // Prevent min from being greater than max
      if (minVal >= maxVal) {
        if (event.target === minSlider) {
          maxSlider.value = minVal + 1;
        } else {
          minSlider.value = maxVal - 1;
        }
      }

      const currentMin = parseInt(minSlider.value);
      const currentMax = parseInt(maxSlider.value);

      // Update display values
      minValue.textContent = `${currency}${currentMin}`;
      maxValue.textContent = `${currency}${currentMax}`;

      // Update visual range
      const minPercent = (currentMin / maxPrice) * 100;
      const maxPercent = (currentMax / maxPrice) * 100;
      
      sliderRange.style.left = `${minPercent}%`;
      sliderRange.style.right = `${100 - maxPercent}%`;
    }

    function submitForm() {
      const form = document.getElementById('FacetFiltersFormMobile');
      if (form) {
        // Create form data and submit
        const formData = new FormData(form);
        const searchParams = new URLSearchParams(formData);
        FacetFiltersForm.renderPage(searchParams.toString());
      }
    }

    // Add event listeners
    minSlider.addEventListener('input', updateSliderValues);
    maxSlider.addEventListener('input', updateSliderValues);
    minSlider.addEventListener('change', submitForm);
    maxSlider.addEventListener('change', submitForm);

    // Initialize values
    updateSliderValues();
  }
}

function handleMobileRadioChange(event) {
  const radio = event.target;
  const selectedValue = radio.value;
  const paramName = radio.dataset.paramName;
  
  // Find the mobile form
  const mobileForm = document.getElementById('FacetFiltersFormMobile');
  if (!mobileForm) {
    console.error('Mobile form not found');
    return;
  }
  
  // Clear existing hidden inputs for this parameter
  const existingInputs = mobileForm.querySelectorAll(`input[name="${paramName}"]`);
  existingInputs.forEach(input => input.remove());
  
  // Add new hidden input if a value is selected (not empty)
  if (selectedValue && selectedValue !== '') {
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = paramName;
    hiddenInput.value = selectedValue;
    hiddenInput.classList.add('mobile-facets__checkbox'); // Add class for compatibility
    mobileForm.appendChild(hiddenInput);
  }
  
  // Trigger the existing FacetFiltersForm submission logic
  const facetFiltersForm = mobileForm.closest('facet-filters-form');
  if (facetFiltersForm) {
    const formData = new FormData(mobileForm);
    const searchParams = new URLSearchParams(formData).toString();
    facetFiltersForm.onSubmitForm(searchParams, event);
  } else {
    // Fallback to direct submission
    const formData = new FormData(mobileForm);
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }
}


// Extend the existing FacetFiltersForm.renderFilters method to handle dropdowns
const originalRenderFilters = FacetFiltersForm.renderFilters;
FacetFiltersForm.renderFilters = function(html, event) {
  // Call the original method
  originalRenderFilters.call(this, html, event);
  
  // Update dropdowns
  FacetDropdownHandler.updateDropdowns(html);
  updateMobileDropdowns(html);
  
  // Re-initialize dropdown handlers for any new dropdowns
  new FacetDropdownHandler();
  initializeMobileDropdowns();
};

function updateMobileDropdowns(html) {
  const parser = new DOMParser();
  const parsedHTML = parser.parseFromString(html, 'text/html');
  
  // Find all mobile radio sections in both current page and fetched content
  const currentMobileRadioSections = document.querySelectorAll('.mobile-facets__radio-section');
  const newMobileRadioSections = parsedHTML.querySelectorAll('.mobile-facets__radio-section');
  
  // Update each mobile radio section with new options and selections
  currentMobileRadioSections.forEach(currentSection => {
    const currentLabel = currentSection.querySelector('.mobile-facets__radio-label');
    if (!currentLabel) return;
    
    const matchingNewSection = Array.from(newMobileRadioSections).find(newSection => {
      const newLabel = newSection.querySelector('.mobile-facets__radio-label');
      return newLabel && newLabel.textContent.trim() === currentLabel.textContent.trim();
    });
    
    if (matchingNewSection) {
      // Preserve currently selected radio button values
      const currentRadios = currentSection.querySelectorAll('.mobile-facets__radio-input');
      const selectedValues = Array.from(currentRadios)
        .filter(radio => radio.checked)
        .map(radio => radio.value);
      
      // Replace the radio list content
      const currentRadioList = currentSection.querySelector('.mobile-facets__radio-list');
      const newRadioList = matchingNewSection.querySelector('.mobile-facets__radio-list');
      
      if (currentRadioList && newRadioList) {
        currentRadioList.innerHTML = newRadioList.innerHTML;
        
        // Restore selections
        selectedValues.forEach(value => {
          const radioToSelect = currentRadioList.querySelector(`input[value="${value}"]`);
          if (radioToSelect) {
            radioToSelect.checked = true;
          }
        });
      }
    }
  });
}
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
